import { useState, useEffect } from 'react';
import { type Criterion } from '../types';
import { ArrowRight, CheckCircle2, AlertCircle } from 'lucide-react';
import { validateWeights, normalizeWeights } from '../utils/scoring';

interface WeightsStepProps {
  criteria: Criterion[];
  onNext: (criteria: Criterion[]) => void;
}

export function WeightsStep({ criteria, onNext }: WeightsStepProps) {
  const [localCriteria, setLocalCriteria] = useState<Criterion[]>(criteria);
  const [validation, setValidation] = useState(validateWeights(criteria));

  useEffect(() => {
    setValidation(validateWeights(localCriteria));
  }, [localCriteria]);

  const handleWeightChange = (id: string, value: string) => {
    const numValue = parseFloat(value) || 0;
    setLocalCriteria(
      localCriteria.map((c) => (c.id === id ? { ...c, weight: numValue } : c))
    );
  };

  const handleEqualWeights = () => {
    const equalWeight = 100 / localCriteria.length;
    setLocalCriteria(
      localCriteria.map((c) => ({ ...c, weight: parseFloat(equalWeight.toFixed(2)) }))
    );
  };

  const handleNormalize = () => {
    const normalized = normalizeWeights(localCriteria);
    setLocalCriteria(normalized);
  };

  const handleNext = () => {
    if (!validation.isValid) {
      alert(validation.message);
      return;
    }
    onNext(localCriteria);
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-6">
        <h2 className="text-xl font-semibold text-[var(--text-pri)] mb-2">
          Assign Weights to Criteria
        </h2>
        <p className="text-sm text-[var(--text-sec)] mb-6">
          Weight each criterion by importance. All weights must sum to exactly 100%.
        </p>

        <div className="space-y-3 mb-4">
          {localCriteria.map((criterion) => (
            <div key={criterion.id} className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
              <div className="sm:flex-1">
                <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                  {criterion.name}
                </label>
                <p className="text-xs text-[var(--text-ter)] mb-2">{criterion.description}</p>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={criterion.weight}
                  onChange={(e) => handleWeightChange(criterion.id, e.target.value)}
                  className="w-24 px-3 py-2 border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-blue-500 text-right"
                />
                <span className="text-[var(--text-sec)] w-8">%</span>
              </div>
            </div>
          ))}
        </div>

        <div className="mb-4 flex gap-2">
          <button
            onClick={handleEqualWeights}
            className="px-3 py-2 text-sm text-[var(--text-sec)] hover:text-[var(--text-pri)] hover:bg-[var(--hover-tint)] transition-colors"
          >
            Make All Equal
          </button>
          {!validation.isValid && (
            <button
              onClick={handleNormalize}
              className="px-3 py-2 text-sm text-[var(--text-ter)] hover:text-[var(--text-ter)] hover:bg-[var(--slate-dim)] transition-colors"
            >
              Normalize to 100%
            </button>
          )}
        </div>

        <div className={`p-3  mb-4 ${
          validation.isValid 
            ? 'bg-[var(--green-dim)] border border-[var(--green)]' 
            : 'bg-[var(--mustard-dim)] border border-[var(--mustard)]'
        }`}>
          <div className="flex items-center gap-2">
            {validation.isValid ? (
              <CheckCircle2 className="h-5 w-5 text-[var(--green)]" />
            ) : (
              <AlertCircle className="h-5 w-5 text-[var(--mustard)]" />
            )}
            <span className={`text-sm font-medium ${
              validation.isValid ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : 'text-[var(--mustard)]'
            }`}>
              Total: {validation.total}%
            </span>
          </div>
          {!validation.isValid && (
            <p className="text-xs text-[var(--mustard)] mt-1 ml-7">
              {validation.message}
            </p>
          )}
        </div>

        <div className="mt-6 pt-4 border-t border-[var(--rule)]">
          <button
            onClick={handleNext}
            disabled={!validation.isValid}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:bg-[var(--surface)] disabled:cursor-not-allowed transition-colors"
          >
            {validation.isValid ? (
              <>
                Continue
                <ArrowRight className="h-4 w-4" />
              </>
            ) : (
              'Weights must sum to 100%'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

